import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 0.4,
    conjureDuration: 750,
    resonateDuration: 900,
    deflectDuration: 450,
    dissolveDuration: 750,
    paths: {
        shield: '/assets/effects/shimmering_shield/shield.png',
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.shield);

class ShimmerParticle {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    color: string;

    constructor(cx: number, cy: number, explosion: boolean = false) {
        this.x = cx + (Math.random() - 0.5) * 50;
        this.y = cy + (Math.random() - 0.5) * 50;
        
        if (explosion) {
            const angle = Math.random() * Math.PI * 2;
            const speed = (5 + Math.random() * 15) * 1.5;
            this.vx = Math.cos(angle) * speed;
            this.vy = Math.sin(angle) * speed;
            this.life = 1.0;
        } else {
            this.vx = (Math.random() - 0.5) * 2 * 1.5;
            this.vy = (-1 - Math.random() * 2) * 1.5;
            this.life = 0.5 + Math.random() * 0.5;
        }
        
        this.size = (2 + Math.random() * 4) * 1.5;
        const colors = ['#f0abfc', '#d946ef', '#38bdf8', '#cffafe'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }

    update() {
        this.x += this.vx;
        this.y += this.vy;
        this.vx *= 0.95;
        this.vy *= 0.95;
        this.life -= 0.02;
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = this.life;
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color;
        ctx.shadowBlur = 10 * this.life;
        ctx.fill();
        ctx.restore();
    }
}

class RunicRing {
    cx: number; cy: number;
    radius: number;
    rotation: number;
    speed: number;
    life: number;

    constructor(cx: number, cy: number) {
        this.cx = cx;
        this.cy = cy;
        this.radius = (50 + Math.random() * 100) * 1.5;
        this.rotation = Math.random() * Math.PI * 2;
        this.speed = (Math.random() - 0.5) * 0.05;
        this.life = 1.0;
    }

    update() {
        this.rotation += this.speed;
        this.life -= 0.03;
        this.radius += 2 * 1.5; // Expand outwards
    }

    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        ctx.save();
        ctx.globalAlpha = this.life * 0.5;
        ctx.translate(this.cx, this.cy);
        ctx.rotate(this.rotation);
        
        ctx.beginPath();
        // Outer complex dashed ring
        ctx.arc(0, 0, this.radius, 0, Math.PI * 2);
        ctx.strokeStyle = '#d946ef';
        ctx.lineWidth = 6;
        ctx.setLineDash([15, 20, 5, 20]);
        ctx.stroke();

        // Inner glowing solid ring
        ctx.beginPath();
        ctx.arc(0, 0, this.radius * 0.9, 0, Math.PI * 2);
        ctx.strokeStyle = '#38bdf8';
        ctx.lineWidth = 3;
        ctx.setLineDash([40, 30]);
        ctx.stroke();
        
        // Geometric inner square for extra magical vibe
        ctx.beginPath();
        const sqSize = this.radius * 0.6;
        ctx.rect(-sqSize, -sqSize, sqSize * 2, sqSize * 2);
        ctx.strokeStyle = '#f0abfc';
        ctx.lineWidth = 2;
        ctx.setLineDash([10, 10]);
        ctx.stroke();
        
        ctx.restore();
    }
}

export type ShimmeringShieldEffect = {
    kind: 'shimmeringShield'
    t: number
    tx: number; ty: number;
    isOpponent: boolean;
    data?: {
        phase: 'CONJURE' | 'RESONATE' | 'DEFLECT' | 'DISSOLVE'
        particles: ShimmerParticle[]
        rings: RunicRing[]
        baseY: number
    }
}

export const createShimmeringShieldEffect = (tx: number, ty: number, isOpponent: boolean): ShimmeringShieldEffect => ({
    kind: 'shimmeringShield', t: 0, tx, ty, isOpponent
});

export const renderShimmeringShieldEffect = (
    eff: ShimmeringShieldEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle?: (p: Particle) => void
): boolean => {
    const tConjure = CONFIG.conjureDuration;
    const tResonate = tConjure + CONFIG.resonateDuration;
    const tDeflect = tResonate + CONFIG.deflectDuration;
    const tTotal = tDeflect + CONFIG.dissolveDuration;

    if (!eff.data) {
        const yOffset = eff.isOpponent ? 120 : -120; // Spawn in front of the avatar
        eff.data = {
            phase: 'CONJURE',
            particles: [],
            rings: [],
            baseY: eff.ty + yOffset
        };
    }

    const d = eff.data;

    if (eff.t < tConjure) d.phase = 'CONJURE';
    else if (eff.t < tResonate) d.phase = 'RESONATE';
    else if (eff.t < tDeflect) d.phase = 'DEFLECT';
    else d.phase = 'DISSOLVE';

    let shieldAlpha = 0;
    let currentScale = CONFIG.scale;
    let motionBlur = 0;
    
    // Hovering physics
    const floatY = Math.sin(eff.t * 0.005) * 15;
    let renderY = d.baseY + floatY;

    if (d.phase === 'CONJURE') {
        const p = eff.t / tConjure;
        const easeOut = 1 - Math.pow(1 - p, 3);
        shieldAlpha = easeOut;
        currentScale = CONFIG.scale * (0.5 + 0.5 * easeOut);
        
        // More rings and particles during conjure
        if (Math.random() < 0.4) d.rings.push(new RunicRing(eff.tx, renderY));
        if (Math.random() < 0.8) d.particles.push(new ShimmerParticle(eff.tx, renderY));
        if (Math.random() < 0.8) d.particles.push(new ShimmerParticle(eff.tx, renderY));

    } else if (d.phase === 'RESONATE') {
        shieldAlpha = 1;
        
        // Gentle sparkling - higher volume
        if (Math.random() < 0.6) {
            d.particles.push(new ShimmerParticle(eff.tx, renderY));
            d.particles.push(new ShimmerParticle(eff.tx, renderY));
        }

    } else if (d.phase === 'DEFLECT') {
        shieldAlpha = 1;
        const p = (eff.t - tResonate) / CONFIG.deflectDuration;
        
        // Shield absorbs impact: flashes, squishes slightly, then recoils
        if (p < 0.2 && d.particles.length < 150) {
            // Massive burst of explosive sparks on impact
            for(let i=0; i<60; i++) d.particles.push(new ShimmerParticle(eff.tx, renderY, true));
        }
        
        // Impact squish
        if (p < 0.3) {
            currentScale = CONFIG.scale * (1.0 - p); // Squish
            motionBlur = 10;
        } else {
            const recoilEase = 1 - Math.pow(1 - ((p - 0.3) / 0.7), 2);
            currentScale = CONFIG.scale * (0.7 + 0.3 * recoilEase);
        }

        // Draw deflecting energy flash (increased size by 1.5)
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(eff.tx, renderY);
        ctx.beginPath();
        ctx.arc(0, 0, 225 * Math.sin(p * Math.PI), 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, 225);
        grad.addColorStop(0, '#ffffff');
        grad.addColorStop(0.2, '#f0abfc');
        grad.addColorStop(1, 'rgba(217, 70, 239, 0)');
        ctx.fillStyle = grad;
        ctx.fill();
        ctx.restore();

    } else if (d.phase === 'DISSOLVE') {
        const p = (eff.t - tDeflect) / CONFIG.dissolveDuration;
        shieldAlpha = 1 - p;
        if (shieldAlpha < 0) shieldAlpha = 0;
        currentScale = CONFIG.scale * (1.0 + p * 0.5); // Bloat out slightly
        renderY -= p * 50; // Float upwards as it dissolves
        
        if (Math.random() < 0.8) d.particles.push(new ShimmerParticle(eff.tx, renderY));
    }

    // Update and draw entities
    d.rings = d.rings.filter(r => r.life > 0);
    d.rings.forEach(r => { r.update(); r.draw(ctx); });

    d.particles = d.particles.filter(p => p.life > 0);
    d.particles.forEach(p => { p.update(); p.draw(ctx); });

    if (shieldAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = shieldAlpha;
        ctx.translate(eff.tx, renderY);
        
        // Flip both shields 180 degrees from their original "facing center" orientation:
        // Local player shield points DOWN (away from center).
        // Opponent shield points UP (away from center).
        if (!eff.isOpponent) {
            ctx.scale(1, -1);
        }
        
        ctx.scale(currentScale, currentScale);

        const img = getSprite(CONFIG.paths.shield);
        if (img.complete && img.naturalWidth > 0) {
            ctx.shadowColor = 'rgba(0,0,0,0.8)';
            ctx.shadowBlur = 15;
            ctx.shadowOffsetY = eff.isOpponent ? 15 : -15;
            
            if (motionBlur > 0) {
                ctx.shadowColor = '#d946ef';
                ctx.shadowBlur = motionBlur;
                ctx.shadowOffsetY = 0;
            }

            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2, img.naturalWidth, img.naturalHeight);
        }
        ctx.restore();
    }

    return eff.t < tTotal;
};
